import React from "react";

/**
 * InstrumentOverlay — a quiet toggleable HUD for perception research.
 *
 * Shows perceptual metrics on top of the scene so a team can discuss
 * timing and relationship in shared reference points, not intuition.
 * Off by default. Toggled with I. Its purpose is developmental — it
 * never ships.
 *
 * Bottom-left corner, monospace, low-opacity. Deliberately smaller
 * than the debug panel; the overlay is meant to accompany the frame,
 * not compete with it.
 */

export default function InstrumentOverlay({
  visible,
  frame,          // "frame-002" | "convergence" | string label
  progress,       // { state, phaseT, phaseDuration, phaseFraction }
  params,         // full params — used to derive relationship metrics
  restDistance,   // world-space distance between the two objects at rest
  hasConvergence, // whether this sequence includes convergence phases
}) {
  if (!visible) return null;

  const state = progress?.state || "idle";
  const phaseT = progress?.phaseT ?? 0;
  const phaseDur = progress?.phaseDuration ?? 0;
  const phaseFrac = progress?.phaseFraction ?? 0;

  // Semantic mapping — what the observer is being asked to do.
  const relationshipPhase = {
    "idle": "before",
    "withdrawing": "observing",
    "holding-distance": "registering the distance",
    "rotating": "examining",
    "settling": "confirming",
    "convergence-delay": "quiet pause",
    "converging": "witnessing coherence",
    "done": "after",
  }[state] || "—";

  // Rotation progress — 0 until Act 3 begins, 1 after Act 3 completes.
  let rotationProgress = 0;
  if (state === "rotating") rotationProgress = phaseFrac;
  else if (state === "settling" || state === "convergence-delay" || state === "converging" || state === "done") rotationProgress = 1;

  // Convergence progress — only meaningful in sequences that include
  // Act 5. In Frame 002, no convergence phase exists; suppress.
  let convergenceProgress = null;
  if (hasConvergence) {
    if (state === "converging") convergenceProgress = phaseFrac;
    else if (state === "done") convergenceProgress = 1;
    else if (state === "convergence-delay") convergenceProgress = 0;
    else convergenceProgress = 0;
  }

  // Live distance between the two objects. In Frame 002 this is
  // constant (rest distance). In the convergence prototype it
  // decreases as convergenceProgress runs.
  const distanceScale =
    convergenceProgress !== null && params?.convergenceAmount
      ? 1 - params.convergenceAmount * convergenceProgress
      : 1;
  const liveDistance = (restDistance ?? 0) * distanceScale;

  return (
    <div
      data-testid="instrument-overlay"
      style={{
        position: "fixed",
        left: 16,
        bottom: 16,
        padding: "10px 12px",
        background: "rgba(0,0,0,0.55)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 3,
        color: "rgba(255,255,255,0.75)",
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        fontSize: 10,
        lineHeight: 1.7,
        letterSpacing: "0.04em",
        zIndex: 9,
        minWidth: 240,
        pointerEvents: "none",
      }}
    >
      <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: 4 }}>
        {frame} · instrument
      </div>

      <Row label="camera phase" value={state} />
      <Row label="relationship phase" value={relationshipPhase} />
      <Row
        label="phase progress"
        value={phaseDur > 0 ? `${phaseT.toFixed(1)} / ${phaseDur.toFixed(1)} s (${Math.round(phaseFrac * 100)}%)` : "—"}
      />
      <Row
        label="rotation progress"
        value={`${Math.round(rotationProgress * 100)}%`}
      />
      {convergenceProgress !== null && (
        <Row
          label="convergence progress"
          value={`${Math.round(convergenceProgress * 100)}%`}
        />
      )}
      {restDistance != null && (
        <Row
          label="object distance"
          value={`${liveDistance.toFixed(3)} ${distanceScale < 1 ? `(rest ${restDistance.toFixed(3)})` : ""}`}
        />
      )}

      <div style={{ marginTop: 6, color: "rgba(255,255,255,0.28)", fontSize: 9, fontStyle: "italic" }}>
        I — toggle
      </div>
    </div>
  );
}


function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <span style={{ color: "rgba(255,255,255,0.45)" }}>{label}</span>
      <span style={{ color: "#d9b98a" }}>{value}</span>
    </div>
  );
}
